import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Sparkles, Lock, Zap, ArrowRight, AlertTriangle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import ActivityProgressList from './ActivityProgressList';
import BudleeAIChatPanel from './BudleeAIChatPanel';
import StatsBar from './StatsBar';
import { useEffect, useState, useMemo } from 'react';
import { userActivitiesAPI } from '@/api/userActivities';
import { useLearningPack } from '@/hooks/useLearningPack';
import { usePoints } from '@/context/PointsContext';
import { chapterAnnotationsAPI } from '@/api/chapterAnnotations';
import { basicConceptsAPI } from '@/api/basicConcepts';
import TeachersNotesPanel from './TeachersNotesPanel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function RightPanel({
  totalXP = null,
  journeyMode = false,
  learningPackData = null,
  onStartJourney,
  onOpenLearningPack = null,
  learningPackOpen = false,
  learningPackCurrentIndex = 0,
  currentSelection = { subject: '', chapter: '' },
  chatMode = false,
  onChatClose,
  prefilledText = '',
  onClearPrefilledText,
  prefilledAnchor = null,
  onClearPrefilledAnchor = null,
  prefillAndSendText = '',
  onClearPrefillAndSend = null,
  chapterId = null,
  subjectId = null,
  onResetComplete = null,
  onCitationClick = null,
  onBeforeSend = null,
  onAskBudleeAI = null,
  showNotesPanel = false,
  setShowNotesPanel = () => { },
  teacherNotes = [],
  isLoadingNotes = false,
  // Mobile split props
  isMobileSplit = false,
  isFullScreenJourney = false,
  onRequestFullJourney = null,
  onTogglePdf = null,
  onNavigateToAnnotation = null,
  mergedSectionKeys = null, // Array of section keys for merged PDF sections
}) {
  // Use PointsContext for accurate data from backend
  const { points } = usePoints();
  // Use totalXP prop if provided (for backward compatibility), otherwise use PointsContext
  const displayXP = totalXP !== null ? totalXP : (points?.total_xp ?? 0);

  // Get packId for localStorage reset
  // Use chapterId if available, otherwise use learningPackData
  const packId = chapterId ? `chapter_${chapterId}` : (learningPackData?.packId || 'default');
  const packState = useLearningPack(packId);

  // Session-only flag: show progress list only after user opens the pack.
  const [hasStartedPack, setHasStartedPack] = useState(false);

  useEffect(() => {
    if (learningPackOpen) setHasStartedPack(true);
  }, [learningPackOpen]);

  // State to force re-render
  const [, forceUpdate] = useState(0);

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Learning pack items (activities) for this chapter
  const [chapterPackActivities, setChapterPackActivities] = useState([]);
  const [isLoadingChapterPack, setIsLoadingChapterPack] = useState(false);

  const handleStartJourneyClick = () => {
    // Show confirmation dialog instead of immediately resetting
    setShowConfirmDialog(true);
  };

  const handleConfirmReset = async () => {
    // Close dialog
    setShowConfirmDialog(false);
    // Check if valid selection exists (fallback to old check if chapterId not available)
    if (!chapterId && !(currentSelection.subject === 'physics' && currentSelection.chapter === 'ch8')) {
      toast.error('Chapter not selected', {
        duration: 3000,
        description: 'Please select a chapter first'
      });
      return;
    }

    // Determine the packId to reset (MUST match what's used in TextbookViewer)
    // Use the packId we already calculated above (same logic as TextbookViewer)
    const resetPackId = packId; // This is: chapterId ? `chapter_${chapterId}` : (learningPack?.packId || 'default')
    const storageKey = `learningPack_${resetPackId}_progress`;

    console.log('🔄 Resetting journey');
    console.log('  📋 chapterId:', chapterId);
    console.log('  📋 learningPackData?.packId:', learningPackData?.packId);
    console.log('  📋 calculated packId:', resetPackId);
    console.log('  📋 storageKey:', storageKey);

    try {
      // Reset backend database records - try to get chapterId from current selection if not provided
      let chapterIdToReset = chapterId;

      // If chapterId is not provided, try to find it from currentSelection
      // This handles the case where we're resetting hardcoded activities but still want to clear backend
      if (!chapterIdToReset && currentSelection.chapter) {
        // Try to find chapterId from the chapter name/label
        // Note: This is a fallback - ideally chapterId should always be passed
        console.log('⚠️ No chapterId provided, attempting to reset backend by chapter name:', currentSelection.chapter);
      }

      // Always try to reset backend if we have a chapterId
      if (chapterIdToReset) {
        try {
          const result = await userActivitiesAPI.resetChapterActivities(chapterIdToReset);
          console.log('✅ Backend reset result:', result);
          console.log(`✅ Deleted ${result.deleted_count || 0} activity completions from database`);
        } catch (backendError) {
          console.warn('⚠️ Backend reset failed (continuing with localStorage reset):', backendError);
          // Don't fail the entire reset if backend fails
        }
      } else {
        console.log('ℹ️ No chapterId available - skipping backend reset (localStorage only)');
      }

      // Reset localStorage progress - clear the specific key and ALL related keys
      // IMPORTANT: Do this BEFORE calling packState.resetProgress() to avoid race conditions
      try {
        // First, get all keys BEFORE clearing (to avoid iteration issues)
        const allKeys = [...Object.keys(localStorage)];
        let clearedCount = 0;

        // Clear ALL learningPack keys for this chapter (in case of variations)
        allKeys.forEach(key => {
          // Match any learningPack key that contains the chapter ID or packId
          if (key.startsWith('learningPack_')) {
            // Match the key - check if it contains the packId anywhere in the key
            // Format: learningPack_<packId>_progress
            const shouldClear =
              key === storageKey || // Exact match (most reliable)
              (resetPackId && key.includes(resetPackId)) || // Contains packId anywhere (catches variations)
              (chapterId && key.includes(`chapter_${chapterId}`)); // Contains chapter ID if available

            if (shouldClear) {
              const value = localStorage.getItem(key);
              localStorage.removeItem(key);
              console.log(`✅ Removed localStorage key: ${key} (was: ${value ? 'present' : 'empty'})`);
              clearedCount++;
            }
          }
        });

        // Also explicitly clear the exact key (in case it wasn't caught above)
        localStorage.removeItem(storageKey);

        // Verify it's actually cleared
        const verifyKey = localStorage.getItem(storageKey);
        if (verifyKey) {
          console.warn(`⚠️ WARNING: Key ${storageKey} still exists after removal! Value: ${verifyKey}`);
          localStorage.removeItem(storageKey);
        } else {
          console.log(`✅ Verified: Key ${storageKey} is cleared`);
        }

        setHasStartedPack(false);
      } catch (localError) {
        console.error('❌ Error clearing localStorage:', localError);
      }

      // Reset the packState (this updates React state immediately)
      // This will try to save {} to localStorage, but we've already cleared it
      packState.resetProgress();

      // Force a re-render to ensure state is updated
      forceUpdate(prev => prev + 1);

      // Wait a moment to ensure all state updates are complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // Double-check localStorage is cleared (in case something wrote back)
      const finalCheck = localStorage.getItem(storageKey);
      if (finalCheck) {
        console.warn(`⚠️ Key still exists after reset! Force removing again...`);
        localStorage.removeItem(storageKey);
        // Also clear any related keys one more time
        const allKeys = [...Object.keys(localStorage)];
        allKeys.forEach(key => {
          if (key.startsWith('learningPack_') && key.includes(resetPackId)) {
            localStorage.removeItem(key);
            console.log(`✅ Force removed: ${key}`);
          }
        });
      }

      // Force reload packState from localStorage to ensure it reflects the cleared state
      packState.reloadFromStorage();

      toast.success('Journey reset successfully! All activities have been reset.', {
        duration: 2000
      });

      // DON'T call onStartJourney - we want to keep the current selection and PDF view
      // The reset should only clear activity completions, not change the UI state

      // Notify parent component that reset is complete (so it can refresh UI)
      if (onResetComplete) {
        onResetComplete();
      }

      // No page reload - state is already reset, components will re-render automatically
      console.log('✅ Reset complete - Activity status cleared, selection preserved');
    } catch (error) {
      console.error('❌ Error resetting activities:', error);

      // Even if something fails, try to reset localStorage
      try {
        localStorage.removeItem(storageKey);
        const allKeys = Object.keys(localStorage);
        allKeys.forEach(key => {
          if (key.includes(`learningPack_`) && (key.includes(`chapter_${chapterId}`) || key.includes(resetPackId))) {
            localStorage.removeItem(key);
          }
        });
        packState.resetProgress();
        setHasStartedPack(false);
      } catch (localError) {
        console.error('❌ Error in fallback localStorage reset:', localError);
      }

      toast.warning('Progress reset attempted', {
        duration: 3000,
        description: 'Some activities may need manual refresh'
      });

      // Still call onStartJourney and notify parent
      if (onStartJourney) {
        onStartJourney();
      }

      // Force reload packState and notify parent to refresh UI
      packState.reloadFromStorage();
      if (onResetComplete) {
        onResetComplete();
      }

      console.log('✅ Reset attempted (with errors) - UI updated without page reload');
    }
  };

  // Load activities for the selected chapter so we can render the learning pack list
  // even before entering journey mode.
  // NOTE: The backend source here is chapter annotations placed by superadmins.
  // They are treated as Learning Pack activities in the student UI.
  useEffect(() => {
    let cancelled = false;

    const normalizeType = (rawType) => {
      const raw = String(rawType || '').trim();
      const lower = raw.toLowerCase();
      if (lower === 'basicconcepts' || lower === 'basic concepts') return 'Watch';
      if (!raw) return 'Watch';
      // Keep common casing used across the app
      const known = ['Watch', 'Video', 'Explore', 'Practice', 'Curiosity'];
      const match = known.find(k => k.toLowerCase() === lower);
      return match || raw;
    };

    const getDefaultDuration = (type) => {
      switch (type) {
        case 'Curiosity':
          return '2 min';
        case 'Practice':
          return '5 min';
        case 'Watch':
          return '10 min';
        case 'Video':
          return '10 min';
        case 'Explore':
          return '5 min';
        default:
          return '5 min';
      }
    };

    const getDefaultTitle = (type) => {
      switch (type) {
        case 'Watch':
          return 'Learn and Discover';
        case 'Video':
          return 'Watch Video';
        case 'Explore':
          return 'Explore Interactive';
        case 'Practice':
          return 'Practice Questions';
        case 'Curiosity':
          return 'Curiosity Corner';
        default:
          return `${type} Activity`;
      }
    };

    const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const getBaseRewards = (type) => {
      const rewards = {
        Watch: { xp: 5, gems: 0 },
        Video: { xp: 5, gems: 0 },
        Explore: { xp: 3, gems: 0 },
        Practice: { xp: 5, gems: 0 },
        Curiosity: { xp: 3, gems: 0 },
      };
      return rewards[type] || { xp: 5, gems: 0 };
    };



    const load = async () => {
      if (!chapterId) {
        setChapterPackActivities([]);
        return;
      }

      setIsLoadingChapterPack(true);
      try {
        // Fetch annotations
        const [annotationsResult] = await Promise.allSettled([
          chapterAnnotationsAPI.getChapterAnnotations(chapterId),
        ]);

        if (cancelled) return;

        let annotations = [];
        if (annotationsResult.status === 'fulfilled') {
          annotations = annotationsResult.value || [];
        }

        const activities = (annotations || [])
          .map((ann) => {
            const type = normalizeType(ann.annotation_type);
            const baseRewards = getBaseRewards(type);
            const rawTitle = typeof ann.title === 'string' ? ann.title.trim() : '';

            // Some Watch (Learn & Discover) annotations were mistakenly saved with title "Watch Video".
            // Also treat "Watch Video" as a placeholder title for Video activities; the type label + one-liner cover it.
            const resolvedTitle = (() => {
              if (type === 'Watch' && rawTitle === 'Watch Video') return getDefaultTitle('Watch');
              if (type === 'Video' && rawTitle === 'Watch Video') return '';
              if (!rawTitle) {
                return type === 'Video' ? '' : getDefaultTitle(type);
              }
              return rawTitle;
            })();

            const rawSectionName = ann.section_name ?? ann.sectionName ?? null;
            const sectionName = typeof rawSectionName === 'string' ? rawSectionName.trim() : null;

            // Backend now provides a computed section_name for annotations.
            // Use it as the display title whenever present.
            const usableSectionName = sectionName && !sectionName.includes('|') ? sectionName : null;

            const rawSectionNumber = ann.section_number ?? ann.sectionNumber ?? null;
            const sectionNumber = typeof rawSectionNumber === 'string' ? rawSectionNumber.trim() : null;

            const rawOneLiner = ann.one_liner ?? ann.oneLiner ?? null;
            const oneLiner = typeof rawOneLiner === 'string' ? rawOneLiner.trim() : null;

            return {
              id: String(ann.id),
              annotation_id: ann.id,
              type,
              duration: ann.duration || getDefaultDuration(type),
              // Keep the activity title as the row title.
              // The section name is rendered as a group header in the right panel.
              title: resolvedTitle,
              description: resolvedTitle ? `${type} activity: ${resolvedTitle}` : undefined,
              one_liner: oneLiner || undefined,
              video_file_url: ann.video_file_url || undefined,
              youtube_url: ann.youtube_url || undefined,
              chapter_id: ann.chapter?.id || ann.chapter || chapterId,
              section_name: usableSectionName,
              section_number: sectionNumber,
              xpReward: baseRewards.xp,
              gemsReward: baseRewards.gems,
            };
          })
          // Keep only supported types for the progress list
          .filter((it) => ['Watch', 'Video', 'Explore', 'Practice', 'Curiosity'].includes(it.type));



        setChapterPackActivities(activities);
      } catch (e) {
        if (!cancelled) {
          setChapterPackActivities([]);
        }
      } finally {
        if (!cancelled) setIsLoadingChapterPack(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [chapterId]);

  const resolvedItems = Array.isArray(learningPackData?.items) && learningPackData.items.length > 0
    ? learningPackData.items
    : chapterPackActivities;

  const resolvedCompletedItems = learningPackData?.completedItems || packState.completedItems;

  // Simple check: has student completed ANY activity?
  // Used to hide "Start Learning Pack" button after first completion
  const hasAnyCompletion = useMemo(() => {
    // Handle both array format (from backend) and object format (from packState)
    let completedCount = 0;

    if (Array.isArray(resolvedCompletedItems)) {
      completedCount = resolvedCompletedItems.length;
    } else if (resolvedCompletedItems && typeof resolvedCompletedItems === 'object') {
      // It's an object like {356: {...}, 363: {...}}
      completedCount = Object.keys(resolvedCompletedItems).length;
    }

    console.log('🔍 [RightPanel] Completion check:', {
      learningPackDataCompletedItems: learningPackData?.completedItems,
      packStateCompletedItems: packState.completedItems,
      resolvedCompletedItems,
      isArray: Array.isArray(resolvedCompletedItems),
      isObject: typeof resolvedCompletedItems === 'object',
      completedCount,
      hasAnyCompletion: completedCount > 0
    });

    return completedCount > 0;
  }, [resolvedCompletedItems]);

  const shouldShowLearningPackProgress = hasStartedPack || hasAnyCompletion;

  console.log('🎯 [RightPanel] Button visibility:', {
    hasStartedPack,
    hasAnyCompletion,
    shouldShowLearningPackProgress,
    buttonWillShow: !hasStartedPack && !(hasStartedPack || hasAnyCompletion)
  });
  const resolvedCurrentIndex = (() => {
    // Parent-controlled index should win so the right list stays in sync
    if (typeof learningPackCurrentIndex === 'number' && learningPackCurrentIndex >= 0) {
      return learningPackCurrentIndex;
    }
    const idxFromParent = typeof learningPackData?.currentIndex === 'number' ? learningPackData.currentIndex : null;
    if (idxFromParent !== null && idxFromParent >= 0) return idxFromParent;
    if (!resolvedItems || resolvedItems.length === 0) return 0;
    const firstIncomplete = resolvedItems.findIndex((it) => !packState.isItemCompleted(it.id));
    return firstIncomplete === -1 ? Math.max(0, resolvedItems.length - 1) : firstIncomplete;
  })();

  const handleStartLearningPack = () => {
    // If we have activities, default to the first incomplete one
    const firstIncompleteIndex = resolvedItems.findIndex((it) => !packState.isItemCompleted(it.id));
    const targetIndex = firstIncompleteIndex === -1 ? 0 : firstIncompleteIndex;
    const targetItem = resolvedItems[targetIndex];

    if (onNavigateToAnnotation && targetItem?.annotation_id) {
      onNavigateToAnnotation(targetItem.annotation_id);
    } else {
      setHasStartedPack(true);
      if (typeof onOpenLearningPack === 'function') {
        onOpenLearningPack(resolvedItems, targetIndex);
      }
    }

    // If parent requests full-screen journey mode on mobile, trigger it
    if (typeof onRequestFullJourney === 'function') {
      try { onRequestFullJourney(); } catch (e) { /* ignore */ }
    }
  };

  const handleSelectPackIndex = (index) => {
    const targetItem = resolvedItems[index];

    if (onNavigateToAnnotation && targetItem?.annotation_id) {
      onNavigateToAnnotation(targetItem.annotation_id);
    } else if (typeof onOpenLearningPack === 'function') {
      onOpenLearningPack(resolvedItems, index);
    }
  };


  return (
    <div className="w-full h-full relative">
      {/* Chat Panel - always mounted to preserve state */}
      <div className={chatMode ? "h-full w-full relative z-50 pointer-events-auto" : "hidden"}>
        <BudleeAIChatPanel
          totalXP={displayXP}
          onClose={onChatClose}
          prefilledText={prefilledText}
          onClearPrefilledText={onClearPrefilledText}
          prefilledAnchor={prefilledAnchor}
          onClearPrefilledAnchor={onClearPrefilledAnchor}
          prefillAndSendText={prefillAndSendText}
          onClearPrefillAndSend={onClearPrefillAndSend}
          chapterId={chapterId}
          subjectId={subjectId}
          pageContext="learn"
          onCitationClick={onCitationClick}
          onBeforeSend={onBeforeSend}
          // Mobile split props forwarded to chat panel
          isMobileSplit={isMobileSplit}
          isFullScreenJourney={isFullScreenJourney}
          onRequestFullJourney={onRequestFullJourney}
          onTogglePdf={onTogglePdf}
        />
      </div>

      <div
        className={`w-full h-full relative bg-[hsl(var(--sidebar-bg))] border-l border-[hsl(var(--card-border))] overflow-y-auto ${chatMode ? 'hidden' : ''}`}
      >
        <TeachersNotesPanel
          isOpen={showNotesPanel}
          onClose={() => setShowNotesPanel(false)}
          notes={teacherNotes}
          isLoading={isLoadingNotes}
          onAskBudleeAI={(text, noteId) => {
            if (onAskBudleeAI) {
              onAskBudleeAI(text, noteId);
              // Optionally close panel or keep it open? 
              // Since chat opens in the right panel usually replacing this content, 
              // or if chat is a overlay... 
              // In LearnPage, chatMode replaces RightPanel content. 
              // So we don't need to manually close, but just in case.
            }
          }}
        />
        <div className="p-6 space-y-6">
          {/* Stats Bar - Using shared component (shown on desktop only, hidden on mobile) */}
          <div className="hidden lg:block">
            <StatsBar totalXP={displayXP} />
          </div>

          {/* Start Fresh Card OR Activity Progress List */}
          {shouldShowLearningPackProgress && resolvedItems && resolvedItems.length > 0 ? (
            <div className="space-y-3">
              <ActivityProgressList
                items={resolvedItems}
                currentIndex={resolvedCurrentIndex}
                completedItems={resolvedCompletedItems}
                onSelectIndex={handleSelectPackIndex}
                multipleSectionKeys={mergedSectionKeys}
              />
              {!hasStartedPack && !hasAnyCompletion && resolvedCompletedItems !== undefined && (
                <Button
                  onClick={handleStartLearningPack}
                  disabled={isLoadingChapterPack}
                  className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-[hsl(var(--main-bg))] font-bold uppercase transition-opacity duration-300 opacity-100 animate-in fade-in"
                >
                  Start Learning Pack
                </Button>
              )}

            </div>
          ) : (() => {
            // Debug: Log why Start Fresh card is being evaluated
            // Wait for backend loading to complete before showing card
            const hasBackendDataLoaded = !isLoadingChapterPack;
            const showCard = (chapterId || (currentSelection.subject === 'physics' && currentSelection.chapter === 'ch8'))
              && resolvedCompletedItems !== undefined
              && !hasAnyCompletion
              && hasBackendDataLoaded; // Only show after backend loading completes

            console.log('🎴 [Start Fresh Card] Render decision:', {
              chapterId,
              isLoadingChapterPack,
              hasBackendDataLoaded,
              hasCompletedItemsData: resolvedCompletedItems !== undefined,
              resolvedCompletedItems,
              hasAnyCompletion,
              shouldShowCard: showCard,
              shouldShowLearningPackProgress
            });

            return showCard;
          })() && (
            <Card className="bg-gradient-to-br from-[hsl(var(--purple-accent))]/20 to-[hsl(var(--primary))]/20 border-[hsl(var(--primary))]/30 overflow-hidden relative transition-opacity duration-300 animate-in fade-in">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[hsl(var(--primary))]/30 to-transparent rounded-full blur-2xl" />
              <CardHeader>
                <Badge className="w-fit bg-gradient-to-r from-[hsl(var(--purple-accent))] to-[hsl(var(--primary))] text-foreground border-0 font-bold uppercase text-xs px-3 py-1">
                  START FRESH
                </Badge>
                <CardTitle className="text-xl font-bold text-foreground mt-3">
                  Learn this chapter from start
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Let Budlee AI guide you step by step — from basics to mastery.
                </p>
                <div className="flex items-center justify-center my-4">
                  <div className="w-24 h-24 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--purple-accent))] rounded-2xl flex items-center justify-center shadow-xl glow-teal transform rotate-12">
                    <Sparkles className="w-12 h-12 text-[hsl(var(--main-bg))]" />
                  </div>
                </div>
                <Button
                  onClick={handleStartLearningPack}
                  className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-[hsl(var(--main-bg))] font-bold uppercase py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Start My Journey
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Confirmation Dialog for Reset */}
          <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
            <AlertDialogContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <AlertDialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-yellow-500" />
                  </div>
                  <AlertDialogTitle className="text-xl font-bold text-white">
                    Reset Your Journey?
                  </AlertDialogTitle>
                </div>
                <AlertDialogDescription className="text-gray-300 pt-2">
                  Are you sure you want to reset all activity progress for this chapter? This will:
                  <ul className="list-disc list-inside mt-3 space-y-1 text-sm text-gray-300">
                    <li className="text-gray-300">Clear all completed activities</li>
                    <li className="text-gray-300">Reset your progress to 0%</li>
                    <li className="text-gray-300">Remove all activity completion records</li>
                  </ul>
                  <p className="mt-3 font-semibold text-white">
                    This action cannot be undone.
                  </p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2 sm:gap-0">
                <AlertDialogCancel
                  onClick={() => setShowConfirmDialog(false)}
                  className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600 hover:text-white"
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirmReset}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  Confirm Reset
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Teachers Notes Card - Only show if notes exist */}
          {teacherNotes.length > 0 && (
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="text-xl font-bold text-foreground">
                  Teacher's Notes
                </CardTitle>
                <Badge variant="secondary" className="bg-purple-100 text-purple-700 hover:bg-purple-200">
                  {teacherNotes.length} New
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center border border-purple-200">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <p className="text-sm text-muted-foreground">
                      View notes and resources shared by your teacher for this chapter.
                    </p>
                    <Button
                      variant="outline"
                      className="w-full border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
                      onClick={() => setShowNotesPanel(true)}
                    >
                      View Notes
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}



          {/* Daily Quests Card removed per request */}
        </div>

      </div>
    </div>
  );
}
